import React, { useState } from 'react';
import UserApi from 'apis/UserApi';
import { useNavigate } from 'react-router-dom';
import { useError } from 'hooks/ErrorContext';
import { useSuccess } from 'hooks/SuccessContext';

function GenerateGatePass({ onClose = () => {}, onSubmit = () => {} }) {
  const navigate = useNavigate();
  const { showError } = useError();
  const { showMessage } = useSuccess();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [guestCount, setGuestCount] = useState(1);
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    duration: 1,
    vehicleNumber: '',
    vehicleType: '',
    purpose: '',
    numberOfGuests: 1
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleVehicleTypeSelect = (type) => {
    setFormData(prev => ({
      ...prev,
      vehicleType: type
    }));
  };

  const handleDurationSelect = (hours) => {
    setFormData(prev => ({
      ...prev,
      duration: hours
    }));
  };

  const formatTimeToAMPM = (time) => {
    try {
      // Handle empty or invalid time
      if (!time) return '';

      // If time is already in AM/PM format, return as is
      if (time.toLowerCase().includes('am') || time.toLowerCase().includes('pm')) {
        return time;
      }

      // Convert 24-hour format to 12-hour format with AM/PM
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return time; // Return original time if formatting fails
    }
  };

  const calculateValidTill = (date, time, duration) => {
    try {
      // Parse the time
      let [hours, minutes] = time.split(':');
      let hour = parseInt(hours);
      let minute = parseInt(minutes);

      // Create date object with the given date and time
      const visitDateTime = new Date(date);
      visitDateTime.setHours(hour, minute, 0, 0);

      // Add duration hours
      const validTill = new Date(visitDateTime.getTime() + (duration * 60 * 60 * 1000));

      // Format the output with AM/PM
      return validTill.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error calculating valid till:', error);
      return time; // Return original time if calculation fails
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Validate required fields
      if (!formData.date || !formData.time || !formData.vehicleNumber || !formData.purpose || !formData.vehicleType) {
        showError('Please fill in all required fields');
        return;
      }

      // Calculate valid_till by adding duration to visit date and time
      const validTill = calculateValidTill(formData.date, formData.time, formData.duration);
      console.log(validTill)

      // Format the data according to DTO requirements
      const submitData = {
        date: formData.date,
        time: formData.time,
        duration: parseInt(formData.duration),
        numberOfGuests: parseInt(formData.numberOfGuests),
        purpose: formData.purpose,
        vehicleNumber: formData.vehicleNumber.trim().toUpperCase(),
        vehicleType: formData.vehicleType,
        valid_till: validTill
      };

      console.log('Formatted submit data:', submitData); // Debug log

      // Validate dates
      const now = new Date();
      now.setHours(0, 0, 0, 0); // Reset time part for date comparison
      const visitDate = new Date(formData.date);

      if (visitDate < now) {
        showError('Visit date cannot be in the past');
        return;
      }

      // Validate duration
      if (submitData.duration < 1 || submitData.duration > 24) {
        showError('Duration must be between 1 and 24 hours');
        return;
      }

      // Validate number of guests
      if (submitData.numberOfGuests < 1) {
        showError('Number of guests must be at least 1');
        return;
      }

      await onSubmit(submitData);
      showMessage('Gate pass request submitted successfully');
      setSuccess(true);
      onClose();
    } catch (error) {
      console.error('Error submitting gate pass:', error);
      showError(error.message || 'Failed to submit gate pass request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-lg max-w-2xl mx-auto p-8">
          <div className="text-center">
            <i className="fas fa-check-circle text-5xl text-green-500 mb-4"></i>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Gate Pass Request Submitted
            </h2>
            <p className="text-gray-600 mb-6">
              Your request has been successfully submitted and is being processed.
            </p>
            <button
              onClick={() => setSuccess(false)}
              className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-700 hover:to-blue-800 transition-all duration-300"
            >
              Submit Another Request
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto fixed inset-0 bg-black/50 flex justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-lg max-w-2xl mx-auto hover:shadow-xl transition-all duration-300 h-fit">
        <div className="bg-gradient-to-r from-maroon-600 to-maroon-800 p-2 rounded-t-xl relative overflow-hidden flex items-center justify-between">
          <div className="absolute opacity-10"></div>
          <div>
            <h1 className="text-base font-bold text-white font-inter mb-2 relative">
              Gate Pass Request
            </h1>
            <p className="text-blue-100 text-sm font-medium relative">
              Fill out the form below to request a gate pass for your visit
            </p>
          </div>

          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-8">
          <div className="space-y-6 pb-6 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="fas fa-calendar text-blue-600"></i>
              </div>
              <h2 className="text-base font-semibold text-gray-900">
                Visit Details
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="relative">
                <label className="block text-gray-600 font-medium mb-2 text-sm">
                  <i className="fas fa-calendar-alt mr-2 text-blue-500"></i>
                  Date
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className="w-full border border-gray-200 rounded-lg p-1 pl-10 focus:ring-2 focus:ring-blue-500 focus:outline-none hover:border-gray-300 transition-all"
                  required
                />
              </div>
              <div className="relative">
                <label className="block text-gray-600 font-medium mb-2 text-sm">
                  <i className="fas fa-clock mr-2 text-blue-500"></i>
                  Time
                </label>
                <input
                  type="time"
                  name="time"
                  value={formData.time}
                  onChange={handleInputChange}
                  className="w-full border border-gray-200 rounded-lg p-1 pl-10 focus:ring-2 focus:ring-blue-500 focus:outline-none hover:border-gray-300 transition-all"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-gray-600 font-medium mb-3 text-sm">
                Duration
              </label>
              <div className="flex flex-wrap gap-3">
                {[1, 2, 4, 8].map((hours) => (
                  <button
                    key={hours}
                    type="button"
                    onClick={() => handleDurationSelect(hours)}
                    className={`px-4 py-2 rounded-full font-medium text-sm transition-all duration-200 flex items-center space-x-2 ${
                      formData.duration === hours
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                    }`}
                  >
                    <i className="fas fa-clock text-blue-500"></i>
                    <span>
                      {hours} hour{hours > 1 ? "s" : ""}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6 pb-6 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="fas fa-car text-blue-600"></i>
              </div>
              <h2 className="text-base font-semibold text-gray-900">
                Vehicle Information
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-600 font-medium mb-2 text-sm">
                  <i className="fas fa-hashtag mr-2 text-blue-500 text-sm"></i>
                  Vehicle Number
                </label>
                <input
                  type="text"
                  name="vehicleNumber"
                  value={formData.vehicleNumber}
                  onChange={handleInputChange}
                  className="text-base uppercase w-full border border-gray-200 rounded-lg p-1 focus:ring-2 focus:ring-blue-500 focus:outline-none hover:border-gray-300 transition-all"
                  required
                  placeholder="Enter vehicle number"
                />
              </div>
              <div>
                <label className="block text-gray-600 font-medium mb-1 text-sm">
                  Vehicle Type
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: "car", icon: "fa-car", label: "Car" },
                    { value: "bike", icon: "fa-motorcycle", label: "Bike" },
                    { value: "other", icon: "fa-truck", label: "Other" },
                  ].map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => handleVehicleTypeSelect(type.value)}
                      className={`p-3 rounded-lg border transition-all group ${
                        formData.vehicleType === type.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      <i
                        className={`fas ${type.icon} text-2xl mb-2 ${
                          formData.vehicleType === type.value
                            ? 'text-blue-500'
                            : 'text-gray-400 group-hover:text-blue-500'
                        }`}
                      ></i>
                      <span className={`block text-sm ${
                        formData.vehicleType === type.value
                          ? 'text-blue-600'
                          : 'text-gray-600 group-hover:text-blue-600'
                      }`}>
                        {type.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6 pb-6 border-b border-gray-100">
            <h2 className="text-xl font-semibold text-gray-900">
              Guest Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-600 font-medium mb-2">
                  Number of Guests
                </label>
                <div className="flex items-center space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      const newCount = Math.max(1, guestCount - 1);
                      setGuestCount(newCount);
                      setFormData(prev => ({ ...prev, numberOfGuests: newCount }));
                    }}
                    className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"
                  >
                    <i className="fas fa-minus text-gray-600"></i>
                  </button>
                  <span className="text-xl font-semibold text-gray-900 w-12 text-center">
                    {guestCount}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      const newCount = guestCount + 1;
                      setGuestCount(newCount);
                      setFormData(prev => ({ ...prev, numberOfGuests: newCount }));
                    }}
                    className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"
                  >
                    <i className="fas fa-plus text-gray-600"></i>
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-gray-600 font-medium mb-2">
                  Purpose of Visit
                </label>
                <select
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleInputChange}
                  className="w-full border border-gray-200 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  required
                >
                  <option value="">Select purpose</option>
                  <option value="meeting">Meeting</option>
                  <option value="delivery">Delivery</option>
                  <option value="personal">Personal</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>

          <div className="pt-6">
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-maroon-600 to-maroon-800 text-white px-6 py-4 rounded-lg font-medium hover:from-blue-700 hover:to-blue-800 transition-all duration-300 flex items-center justify-center space-x-2 transform hover:translate-y-[-1px] hover:shadow-lg"
            >
              {loading ? (
                <>
                  <i className="fas fa-circle-notch fa-spin"></i>
                  <span>Processing your request...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane mr-2"></i>
                  <span>Submit Gate Pass Request</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default GenerateGatePass;


